import { useMemo, useCallback } from "react";
import { Grid, List, ListItem, Typography, Theme } from "@mui/material";
import { getResourceFieldPermissions } from "src/API/helpers";
import { PolicyResource } from "@vmosolutions/oc-interfaces";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { useSelector } from "react-redux";
import { IExtendedJSONSchema6, ExtendedTypes } from "./CustomFieldsV2";

interface ICustomValuesProps {
  /** JSON Schema revision 6.0 required - schema is used to display labels */
  schema: IExtendedJSONSchema6 | null;
  /** All Custom values used inside the app are stored inside a nested object, assign it here */
  customValues: any;
  /** mosaic window pane element if available */
  mosaicPane?: HTMLElement;
  /** If permission required, include resource name */
  resource?: PolicyResource;
  /** Display a text header above the custom fields indicating they are custom */
  displayHeader: string;
}

const getPropertyValue = (customValues: any, defaultValues: any, key: string, type: ExtendedTypes): string => {
  switch (true) {
    // injected string/numbers/booleans.
    case customValues && type !== "object" && customValues?.[key] !== undefined:
      return customValues[key].toString();
    // injected unitvalue.
    case customValues &&
      type === "object" &&
      customValues?.[key]?.value !== undefined &&
      customValues?.[key]?.unit !== undefined:
      return `${customValues[key].value.toString()} ${customValues[key].unit.toString()}`;
    // default unitvalue.
    case type !== "object" && defaultValues?.[key] !== undefined:
      return `${defaultValues?.[key].toString()}`;
    // default string/numbers/booleans.
    case type === "object" && defaultValues?.[key]?.value !== undefined && defaultValues?.[key]?.unit !== undefined:
      return `${defaultValues?.[key]?.default?.value.toString() ?? ""} ${defaultValues?.[key]?.unit.toString() ?? ""}`;
    default:
      return "";
  }
};

interface ICustomValues {
  [property: string]: {
    label: string;
    value: string;
    description: string;
  };
}

export const CustomValues = (props: ICustomValuesProps): JSX.Element => {
  const { schema, customValues, mosaicPane, resource, displayHeader } = props;
  const userPolicies = useSelector((state: IRootState) => state.authentication.attributes.policies);

  // To check the field permissions
  const checkFieldPermissionFunction = useMemo(
    () => getResourceFieldPermissions(userPolicies, resource),
    [resource, userPolicies]
  );

  const hasFieldPermissions = useCallback(
    (fieldName: string) => (resource ? checkFieldPermissionFunction(fieldName) : true),
    [checkFieldPermissionFunction, resource]
  );

  // Set default values (defined in properties by schema, only if not initial values are note found for properties)
  const customLabelValues: ICustomValues = useMemo(() => {
    const obj: { [key: string]: any } = {};
    if (schema?.properties) {
      for (const [key, property] of Object.entries(schema?.properties)) {
        // console.log(property);
        if (hasFieldPermissions(key)) {
          // Check to see if user has permission to few this info before adding it.
          obj[key] = {
            label: property?.title ?? "",
            value: getPropertyValue(customValues ?? {}, property?.default ?? {}, key, property.type),
            description: property?.description ?? "",
          };
        }
      }
    }
    return obj;
  }, [customValues, schema, hasFieldPermissions]);

  const customValuesStyles = (theme: Theme): any => ({
    CustomHeader: {
      margin: "10px 0px 0px 0px",
      fontWeight: "300px",
      fontSize: 10,
      textTransform: "uppercase",
      color: theme.custom.forms.labels,
    },
    summaryItemLabel: {
      fontSize: 11,
      fontWeight: "bold",
    },
    summaryItemValue: {
      fontSize: 11,
      fontWeight: "normal",
    },
    summaryItem: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "nowrap",
      justifyContent: "space-between",
      paddingTop: "2px",
      paddingBottom: "2px",
    },
  });

  // Render Custom Fields
  return (
    <Grid container direction="column">
      {displayHeader && <Typography sx={(theme) => customValuesStyles(theme).CustomHeader}>{displayHeader}</Typography>}
      <List disablePadding>
        {Object.entries(customLabelValues).map(([property, obj]) => {
          const { label, value } = obj;
          return (
            <ListItem key={property} sx={(theme) => customValuesStyles(theme).summaryItem}>
              <Typography sx={(theme) => customValuesStyles(theme).summaryItemLabel}>{label}</Typography>
              <Typography sx={(theme) => customValuesStyles(theme).summaryItemValue}>{value}</Typography>
            </ListItem>
          );
        })}
      </List>
    </Grid>
  );
};
